'use client';
import type { LucideIconName } from '../assets/icons';
import { LUCIDE_ICONS } from '../assets/icons';
import type { SVGAttributes } from 'react';
import { forwardRef } from 'react';

interface LucideIconProps extends SVGAttributes<SVGSVGElement> {
  name: LucideIconName;
  size?: number | string;
  /** @deprecated Use className with w-X h-X instead */
  boxSize?: number | string;
}

/**
 * This component is a client component that renders a Lucide icon.
 * It is used to render a Lucide icon in the UI.
 * If you get an error `Only plain objects can be passed to Client Components from Server Components.`,
 * you can use this component instead of the `Icon` component.
 */
export const LucideIcon = forwardRef<SVGSVGElement, LucideIconProps>(
  ({ name, size, boxSize, className, width, height, ...props }, ref) => {
    const LucideIconComponent = LUCIDE_ICONS[name] as React.ElementType;

    if (!LucideIconComponent) {
      console.error(`Icon "${name}" not found in Icons, please add icon in src/assets/icons.ts`);
      return null;
    }

    // Support legacy boxSize prop by converting to width/height
    // Default to 24px (lucide-react standard) to prevent SVGs from filling container
    const resolvedSize = size ?? boxSize ?? 24;
    const resolvedWidth = width ?? resolvedSize;
    const resolvedHeight = height ?? resolvedSize;

    return (
      <LucideIconComponent ref={ref} className={className} width={resolvedWidth} height={resolvedHeight} {...props} />
    );
  },
);
